import React, { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { toast } from 'react-toastify';
import api from '../../services/api';
import { Badge, Button, Card, LoadingState } from '../ui';
import {
  CheckIcon,
  MagnifyingGlassIcon,
  UserGroupIcon,
  UserMinusIcon,
  UserPlusIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';

const closeAndReset = (setOpen, setSearch, setSelected) => {
  setOpen(false);
  setSearch('');
  setSelected([]);
};

const getAgentAvatarUrl = (agent) => (
  agent.avatar_url
  || null
);

const getAgentInitials = (agent) => {
  const nameInitials = [agent.first_name, agent.last_name]
    .filter(Boolean)
    .map((part) => String(part).trim().charAt(0))
    .filter(Boolean)
    .join('')
    .slice(0, 2);

  return (nameInitials || String(agent.username || '?').replace(/^@/, '').charAt(0) || '?')
    .toUpperCase();
};

const AgentAvatar = ({ agent, className = 'h-10 w-10' }) => {
  const [imageFailed, setImageFailed] = useState(false);
  const avatarUrl = getAgentAvatarUrl(agent);
  const displayName = [agent.first_name, agent.last_name].filter(Boolean).join(' ')
    || (agent.username ? `@${agent.username}` : 'Agent');

  useEffect(() => {
    setImageFailed(false);
  }, [avatarUrl]);

  return (
    <span
      className={`inline-flex shrink-0 items-center justify-center overflow-hidden rounded-full bg-blue-100 text-sm font-semibold text-blue-700 ${className}`}
      aria-label={`${displayName} avatar`}
    >
      {avatarUrl && !imageFailed ? (
        <img
          src={avatarUrl}
          alt=""
          className="h-full w-full object-cover"
          onError={() => setImageFailed(true)}
        />
      ) : (
        <span aria-hidden="true">{getAgentInitials(agent)}</span>
      )}
    </span>
  );
};

const ProjectAgentsManager = ({ projectId }) => {
  const queryClient = useQueryClient();
  const [isAddOpen, setIsAddOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [selectedIds, setSelectedIds] = useState([]);

  const linkedQuery = useQuery(
    ['projectAgents', projectId],
    () => api.get(`/projects/${projectId}/agents`).then((response) => response.data.agents),
    { enabled: Boolean(projectId), keepPreviousData: true }
  );

  const poolQuery = useQuery(
    ['agentPool', projectId, search],
    () => api.get('/agents/pool', {
      params: {
        search: search.trim() || undefined,
        exclude_project: projectId,
      },
    }).then((response) => response.data.agents),
    { enabled: Boolean(projectId) && isAddOpen, keepPreviousData: true }
  );

  const addMutation = useMutation(
    (agentIds) => api.post(`/projects/${projectId}/agents`, { agent_ids: agentIds }),
    {
      onSuccess: ({ data }) => {
        queryClient.invalidateQueries(['projectAgents', projectId]);
        queryClient.invalidateQueries(['agentPool', projectId]);
        closeAndReset(setIsAddOpen, setSearch, setSelectedIds);
        toast.success(`Added: ${data.added}. Skipped: ${data.skipped}.`);
      },
    }
  );

  const removeMutation = useMutation(
    (agentId) => api.delete(`/projects/${projectId}/agents/${agentId}`),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['projectAgents', projectId]);
        queryClient.invalidateQueries(['agentPool', projectId]);
        toast.success('Agent removed from the project.');
      },
    }
  );

  const linkedAgents = linkedQuery.data || [];
  const poolAgents = poolQuery.data || [];

  const toggleAgent = (agentId) => {
    setSelectedIds((current) => (
      current.includes(agentId)
        ? current.filter((id) => id !== agentId)
        : [...current, agentId]
    ));
  };

  const confirmRemove = (agent) => {
    const displayName = agent.username ? `@${agent.username}` : `${agent.first_name || ''} ${agent.last_name || ''}`.trim();
    if (window.confirm(`Remove ${displayName || 'this agent'} from the project?`)) {
      removeMutation.mutate(agent.id);
    }
  };

  if (linkedQuery.isLoading) {
    return <LoadingState message="Loading agents..." />;
  }

  return (
    <div className="mt-4 space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-medium text-gray-900">Agents</h3>
          <p className="text-sm text-gray-500">
            One agent can be linked to several projects.
          </p>
        </div>
        <Button variant="primary" onClick={() => setIsAddOpen(true)}>
          <UserPlusIcon className="mr-2 h-5 w-5" />
          Add agent
        </Button>
      </div>

      <Card>
        {linkedAgents.length === 0 ? (
          <div className="p-8 text-center text-gray-500">
            <UserGroupIcon className="mx-auto mb-3 h-12 w-12 text-gray-300" />
            <p className="font-medium">No agents are linked to this project yet</p>
            <Button className="mt-4" variant="primary" onClick={() => setIsAddOpen(true)}>
              <UserPlusIcon className="mr-2 h-5 w-5" />
              Add the first agent
            </Button>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Name</th>
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Username</th>
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Status</th>
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Projects</th>
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Added</th>
                  <th className="px-6 py-3 text-right text-xs font-medium uppercase tracking-wider text-gray-500">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 bg-white">
                {linkedAgents.map((agent) => (
                  <tr key={agent.id} className="hover:bg-gray-50">
                    <td className="whitespace-nowrap px-6 py-4">
                      <div className="flex items-center gap-3">
                        <AgentAvatar agent={agent} />
                        <span className="text-sm font-medium text-gray-900">
                          {[agent.first_name, agent.last_name].filter(Boolean).join(' ') || '—'}
                        </span>
                      </div>
                    </td>
                    <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-600">
                      {agent.username ? `@${agent.username}` : '—'}
                    </td>
                    <td className="whitespace-nowrap px-6 py-4">
                      <Badge variant={agent.is_active ? 'green' : 'gray'}>
                        {agent.is_active ? 'Active' : 'Inactive'}
                      </Badge>
                    </td>
                    <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-600">
                      {agent.projects_count}
                    </td>
                    <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-600">
                      {agent.added_at ? new Date(agent.added_at).toLocaleDateString() : '—'}
                    </td>
                    <td className="whitespace-nowrap px-6 py-4 text-right">
                      <button
                        type="button"
                        onClick={() => confirmRemove(agent)}
                        disabled={removeMutation.isLoading}
                        className="inline-flex items-center gap-1 rounded px-3 py-1.5 text-sm text-red-600 hover:bg-red-50 hover:text-red-800 disabled:opacity-50"
                      >
                        <UserMinusIcon className="h-4 w-4" />
                        Remove
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {isAddOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
          <div className="flex max-h-[80vh] w-full max-w-2xl flex-col overflow-hidden rounded-lg bg-white shadow-xl">
            <div className="flex items-center justify-between border-b border-gray-200 px-6 py-4">
              <h3 className="text-lg font-semibold text-gray-900">Add agents</h3>
              <button
                type="button"
                aria-label="Close"
                onClick={() => closeAndReset(setIsAddOpen, setSearch, setSelectedIds)}
                className="text-gray-400 hover:text-gray-600"
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>

            <div className="border-b border-gray-200 px-6 py-4">
              <div className="relative">
                <MagnifyingGlassIcon className="absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-gray-400" />
                <input
                  type="search"
                  value={search}
                  onChange={(event) => setSearch(event.target.value)}
                  placeholder="Search by username or name"
                  className="w-full rounded-lg border border-gray-300 py-2 pl-10 pr-4 focus:border-blue-500 focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <p className="mt-2 text-sm font-medium text-blue-600">Selected: {selectedIds.length}</p>
            </div>

            <div className="min-h-0 flex-1 overflow-y-auto">
              {poolQuery.isLoading ? (
                <div className="p-8"><LoadingState message="Loading available agents..." /></div>
              ) : poolAgents.length === 0 ? (
                <div className="p-8 text-center text-gray-500">
                  {search ? 'No agents match the search.' : 'All agents are already linked to this project.'}
                </div>
              ) : (
                <div className="divide-y divide-gray-100">
                  {poolAgents.map((agent) => (
                    <label key={agent.id} className="flex cursor-pointer items-center px-6 py-3 hover:bg-gray-50">
                      <input
                        type="checkbox"
                        checked={selectedIds.includes(agent.id)}
                        onChange={() => toggleAgent(agent.id)}
                        className="h-5 w-5 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      />
                      <AgentAvatar agent={agent} className="ml-4 h-10 w-10" />
                      <span className="ml-3 min-w-0 flex-1">
                        <span className="block text-sm font-medium text-gray-900">
                          {[agent.first_name, agent.last_name].filter(Boolean).join(' ') || '—'}
                        </span>
                        <span className="block text-sm text-gray-500">
                          {agent.username ? `@${agent.username}` : 'No username'} · {agent.projects_count} projects
                        </span>
                      </span>
                      <Badge variant={agent.is_active ? 'green' : 'gray'}>
                        {agent.is_active ? 'Active' : 'Inactive'}
                      </Badge>
                    </label>
                  ))}
                </div>
              )}
            </div>

            <div className="flex items-center justify-end gap-3 border-t border-gray-200 bg-gray-50 px-6 py-4">
              <Button variant="secondary" onClick={() => closeAndReset(setIsAddOpen, setSearch, setSelectedIds)}>
                Cancel
              </Button>
              <Button
                variant="primary"
                disabled={selectedIds.length === 0 || addMutation.isLoading}
                onClick={() => addMutation.mutate(selectedIds)}
              >
                {addMutation.isLoading ? (
                  'Adding...'
                ) : (
                  <><CheckIcon className="mr-2 h-5 w-5" />Add {selectedIds.length} agents</>
                )}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProjectAgentsManager;
